/**
 * MeetingScreen — Nachbau der hauseigenen "Gespräche"-Ansicht aus dem
 * bunker.ai Produkt (Besprechungs-Nachbereitung). Aus einem aufgezeichneten
 * Gespräch erzeugt die KI automatisch ein Protokoll: Kernentscheidungen,
 * To-dos mit Verantwortlichen und Fristen sowie nächste Schritte — alles
 * belegbar aus dem Transkript.
 *
 * Rendert ausschließlich den Hauptinhalt (3 Spalten: Gesprächsliste,
 * Transkript, KI-Zusammenfassung); die App-Shell (Sidebar, Header) liefert
 * die übergeordnete Demo-Hülle. Styling ist unter `.bnk-meeting` gescoped,
 * Farben sind die Produkt-Tokens (kein Marketing-Theme). Sämtliche sichtbaren
 * Texte stammen aus dem `demo`-Namespace unter meetingScreen.<scenarioId>.*.
 */

import {
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Filter,
  Flag,
  Lightbulb,
  ListChecks,
  MessagesSquare,
  Search,
  ShieldCheck,
  Sparkles,
  UserCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const C = {
  appBg: 'var(--bnkp-appbg, #0D1117)',
  surface: 'var(--bnkp-surface, #141B27)',
  elevated: 'var(--bnkp-elevated, #1A2334)',
  recessed: 'var(--bnkp-recessed, #0A0F1C)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  text: 'var(--bnkp-text, #E2E8F0)',
  textSec: 'var(--bnkp-text2, #94A3C2)',
  textTer: 'var(--bnkp-text3, #4A5568)',
  primary: 'var(--bnkp-primary, #2D7EFF)',
  primaryHover: 'var(--bnkp-primary2, #1F6BEF)',
  cyan: 'var(--bnkp-cyan, #00D4FF)',
  green: 'var(--bnkp-green, #34D399)',
  amber: 'var(--bnkp-amber, #FBBF24)',
  red: 'var(--bnkp-red, #FB7185)',
  purple: 'var(--bnkp-purple, #7C3AED)',
} as const;

const PREFIX = 'meetingScreen';

interface RecentMeeting {
  title: string;
  date: string;
  duration: string;
}

interface TranscriptLine {
  speaker: string;
  time: string;
  text: string;
}

interface Todo {
  task: string;
  owner: string;
  due: string;
}

// Role → accent colour for participant chips / speaker avatars. Falls back to
// the cyan accent for named people (Frau Mustermann / Herr Beispiel).
const ROLE_ACCENT: Record<string, string> = {
  Projektleitung: C.primary,
  Technik: C.green,
  Vertrieb: C.amber,
  Einkauf: C.purple,
};

function accentFor(speaker: string): string {
  return ROLE_ACCENT[speaker] ?? C.cyan;
}

// Initials for the speaker avatar: roles → first 2 letters, names → initials.
function initialsFor(speaker: string): string {
  const parts = speaker.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return speaker.slice(0, 2).toUpperCase();
}

export function MeetingScreen({
  active = true,
  scenarioId,
  className,
}: {
  active?: boolean;
  scenarioId: string;
  className?: string;
}) {
  const { t } = useTranslation('demo');
  const k = (field: string) => `${PREFIX}.${scenarioId}.${field}`;

  const recent = t(k('meetings'), { returnObjects: true }) as RecentMeeting[];
  const participants = t(k('participants'), { returnObjects: true }) as string[];
  const transcript = t(k('transcript'), { returnObjects: true }) as TranscriptLine[];
  const decisions = t(k('decisions'), { returnObjects: true }) as string[];
  const todos = t(k('todos'), { returnObjects: true }) as Todo[];
  const nextSteps = t(k('nextSteps'), { returnObjects: true }) as string[];

  const currentTitle = t(k('meetingTitle'));

  return (
    <div className={`bnk-meeting${active ? ' bnk-meeting--active' : ''}${className ? ` ${className}` : ''}`}>
      <style>{css}</style>

      {/* ── Header bar ── */}
      <header className="bnk-mtg-head">
        <div className="bnk-mtg-titlewrap">
          <span className="bnk-mtg-iconpill" aria-hidden="true">
            <MessagesSquare size={20} strokeWidth={2.25} />
          </span>
          <div>
            <h1 className="bnk-mtg-title">{t(k('headerTitle'))}</h1>
            <p className="bnk-mtg-sub">{t(k('headerSubtitle'))}</p>
          </div>
        </div>
        <button type="button" className="bnk-mtg-newbtn">
          <Sparkles size={15} strokeWidth={2.25} aria-hidden="true" />
          {t(k('newMeetingButton'))}
        </button>
      </header>

      {/* ── 3-column workspace ── */}
      <div className="bnk-mtg-grid">
        {/* LEFT — recent meetings rail */}
        <aside className="bnk-mtg-rail" aria-label={t(k('recentHeading'))}>
          <label className="bnk-mtg-search">
            <Search size={15} color={C.textSec} aria-hidden="true" />
            <input
              type="search"
              placeholder={t(k('searchPlaceholder'))}
              aria-label={t(k('searchAriaLabel'))}
            />
            <span className="bnk-mtg-filter" aria-hidden="true">
              <Filter size={13} />
              {t(k('filterLabel'))}
            </span>
          </label>

          <div className="bnk-mtg-rail-head">{t(k('recentHeading'))}</div>

          <ul className="bnk-mtg-list">
            {recent.map((m, i) => {
              const selected = i === 0;
              return (
                <li key={`${m.title}-${i}`}>
                  <button
                    type="button"
                    className={`bnk-mtg-item${selected ? ' bnk-mtg-item--active' : ''}`}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <span className="bnk-mtg-item-bar" aria-hidden="true" />
                    <span className="bnk-mtg-item-body">
                      <span className="bnk-mtg-item-title">{m.title}</span>
                      <span className="bnk-mtg-item-meta">
                        <CalendarClock size={12} strokeWidth={2.25} aria-hidden="true" />
                        {m.date}
                        <span className="bnk-mtg-dotsep" aria-hidden="true">·</span>
                        {m.duration}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* CENTER — meeting header + transcript */}
        <section className="bnk-mtg-center" aria-label={currentTitle}>
          <div className="bnk-mtg-meta">
            <h2 className="bnk-mtg-meta-title">{currentTitle}</h2>
            <div className="bnk-mtg-meta-row">
              <span className="bnk-mtg-meta-chip">
                <CalendarClock size={13} strokeWidth={2.25} aria-hidden="true" />
                {t(k('meetingDate'))}, {t(k('meetingTime'))}
              </span>
              <span className="bnk-mtg-meta-chip">
                <Clock size={13} strokeWidth={2.25} aria-hidden="true" />
                {t(k('meetingDuration'))}
              </span>
            </div>
            <div className="bnk-mtg-parts">
              <span className="bnk-mtg-parts-label">{t(k('participantsLabel'))}</span>
              {participants.map((p) => {
                const accent = accentFor(p);
                return (
                  <span
                    key={p}
                    className="bnk-mtg-part"
                    style={{
                      color: accent,
                      background: `${accent}1f`,
                      borderColor: `${accent}59`,
                    }}
                  >
                    <span
                      className="bnk-mtg-part-avatar"
                      style={{ background: accent }}
                      aria-hidden="true"
                    >
                      {initialsFor(p)}
                    </span>
                    {p}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="bnk-mtg-transcript">
            <div className="bnk-mtg-transcript-head">
              <span className="bnk-mtg-transcript-title">
                <FileText size={14} strokeWidth={2.25} aria-hidden="true" />
                {t(k('transcriptHeading'))}
              </span>
              <span className="bnk-mtg-status">
                <span className="bnk-mtg-status-dot" aria-hidden="true" />
                {t(k('transcriptStatus'))}
              </span>
            </div>
            <div className="bnk-mtg-transcript-meta">{t(k('transcriptMeta'))}</div>

            <div className="bnk-mtg-lines">
              {transcript.map((line, i) => {
                const accent = accentFor(line.speaker);
                return (
                  <div className="bnk-mtg-line" key={`${line.time}-${i}`}>
                    <span
                      className="bnk-mtg-avatar"
                      style={{ background: `${accent}26`, color: accent, borderColor: `${accent}59` }}
                      aria-hidden="true"
                    >
                      {initialsFor(line.speaker)}
                    </span>
                    <div className="bnk-mtg-line-body">
                      <div className="bnk-mtg-line-head">
                        <span className="bnk-mtg-speaker" style={{ color: accent }}>
                          {line.speaker}
                        </span>
                        <span className="bnk-mtg-time">{line.time}</span>
                      </div>
                      <p className="bnk-mtg-utter">{line.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* RIGHT — KI summary */}
        <aside className="bnk-mtg-summary" aria-label={t(k('summaryTitle'))}>
          <div className="bnk-mtg-summary-head">
            <span className="bnk-mtg-summary-icon" aria-hidden="true">
              <Sparkles size={16} strokeWidth={2.25} />
            </span>
            <h2 className="bnk-mtg-summary-title">{t(k('summaryTitle'))}</h2>
            <span className="bnk-mtg-summary-badge">{t(k('summaryBadge'))}</span>
          </div>

          <div className="bnk-mtg-summary-scroll">
            {/* Kernentscheidungen */}
            <section className="bnk-mtg-block">
              <h3 className="bnk-mtg-block-head">
                <Lightbulb size={14} strokeWidth={2.25} color={C.amber} aria-hidden="true" />
                {t(k('decisionsHeading'))}
              </h3>
              <ul className="bnk-mtg-decisions">
                {decisions.map((d, i) => (
                  <li className="bnk-mtg-decision" key={i}>
                    <span className="bnk-mtg-decision-mark" aria-hidden="true" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* To-dos */}
            <section className="bnk-mtg-block">
              <h3 className="bnk-mtg-block-head">
                <ListChecks size={14} strokeWidth={2.25} color={C.primary} aria-hidden="true" />
                {t(k('todosHeading'))}
                <span className="bnk-mtg-count">{todos.length}</span>
              </h3>
              <ul className="bnk-mtg-todos">
                {todos.map((todo, i) => {
                  const accent = accentFor(todo.owner);
                  return (
                    <li className="bnk-mtg-todo" key={i}>
                      <span className="bnk-mtg-check" aria-hidden="true">
                        <CheckCircle2 size={17} strokeWidth={2.25} />
                      </span>
                      <div className="bnk-mtg-todo-body">
                        <span className="bnk-mtg-todo-task">{todo.task}</span>
                        <div className="bnk-mtg-todo-meta">
                          <span
                            className="bnk-mtg-todo-owner"
                            style={{ color: accent, background: `${accent}1f`, borderColor: `${accent}4d` }}
                          >
                            <UserCircle size={12} strokeWidth={2.25} aria-hidden="true" />
                            {t(k('ownerLabel'))}: {todo.owner}
                          </span>
                          <span className="bnk-mtg-todo-due">
                            <Flag size={11} strokeWidth={2.5} aria-hidden="true" />
                            {todo.due}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            {/* Nächste Schritte */}
            <section className="bnk-mtg-block">
              <h3 className="bnk-mtg-block-head">
                <ChevronRight size={14} strokeWidth={2.5} color={C.cyan} aria-hidden="true" />
                {t(k('nextStepsHeading'))}
              </h3>
              <ul className="bnk-mtg-next">
                {nextSteps.map((s, i) => (
                  <li className="bnk-mtg-next-item" key={i}>
                    <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <div className="bnk-mtg-summary-foot">
            <span className="bnk-mtg-source">
              <FileText size={12} strokeWidth={2.25} aria-hidden="true" />
              {t(k('sourceChip'))}
            </span>
            <span className="bnk-mtg-verified">
              <ShieldCheck size={12} strokeWidth={2.25} aria-hidden="true" />
              {t(k('verifiedChip'))}
            </span>
          </div>
          <p className="bnk-mtg-summary-note">
            <CheckCheck size={12} strokeWidth={2.25} aria-hidden="true" />
            {t(k('summaryFooter'))}
          </p>
        </aside>
      </div>
    </div>
  );
}

export default MeetingScreen;

const css = `
.bnk-meeting {
  --m-bg: ${C.appBg}; --m-surface: ${C.surface}; --m-elev: ${C.elevated};
  --m-recess: ${C.recessed}; --m-border: ${C.border}; --m-border2: ${C.borderStrong};
  --m-text: ${C.text}; --m-sec: ${C.textSec}; --m-ter: ${C.textTer};
  --m-primary: ${C.primary}; --m-cyan: ${C.cyan}; --m-green: ${C.green};
  font-family: Inter, system-ui, sans-serif;
  color: var(--m-text);
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  padding: clamp(14px, 1.8vw, 22px);
  box-sizing: border-box;
}
.bnk-meeting *, .bnk-meeting *::before, .bnk-meeting *::after { box-sizing: border-box; }

/* ── entrance animation (gated on .bnk-meeting--active) ── */
.bnk-meeting--active .bnk-mtg-head { animation: bnk-mtg-fade .45s ease both; }
.bnk-meeting--active .bnk-mtg-rail { animation: bnk-mtg-slide-l .5s ease .04s both; }
.bnk-meeting--active .bnk-mtg-center { animation: bnk-mtg-fade .5s ease .08s both; }
.bnk-meeting--active .bnk-mtg-summary { animation: bnk-mtg-slide-r .5s ease .12s both; }
@keyframes bnk-mtg-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes bnk-mtg-slide-l { from { opacity: 0; transform: translateX(-14px); } to { opacity: 1; transform: none; } }
@keyframes bnk-mtg-slide-r { from { opacity: 0; transform: translateX(14px); } to { opacity: 1; transform: none; } }

/* ── header ── */
.bnk-mtg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 16px; flex: none;
}
.bnk-mtg-titlewrap { display: flex; align-items: center; gap: 13px; }
.bnk-mtg-iconpill {
  display: grid; place-items: center; width: 42px; height: 42px; flex: none;
  border-radius: 12px; color: var(--m-primary);
  background: rgba(45, 126, 255, 0.1); border: 1px solid rgba(45, 126, 255, 0.22);
}
.bnk-mtg-title { margin: 0; font-size: 21px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.15; }
.bnk-mtg-sub { margin: 3px 0 0; font-size: 13px; color: var(--m-sec); }
.bnk-mtg-newbtn {
  display: inline-flex; align-items: center; gap: 8px; flex: none; cursor: pointer;
  padding: 9px 16px; border-radius: 11px; border: 0; font: inherit;
  font-size: 13.5px; font-weight: 700; color: #fff;
  background: linear-gradient(180deg, ${C.primary} 0%, ${C.primaryHover} 100%);
  box-shadow: 0 6px 16px rgba(45, 126, 255, 0.3); transition: filter .15s, transform .1s;
}
.bnk-mtg-newbtn:hover { filter: brightness(1.08); }
.bnk-mtg-newbtn:active { transform: translateY(1px); }

/* ── 3-column grid ── */
.bnk-mtg-grid {
  flex: 1; min-height: 0;
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 340px;
  gap: 14px;
}

/* ── LEFT rail ── */
.bnk-mtg-rail {
  display: flex; flex-direction: column; min-height: 0;
  background: var(--m-surface); border: 1px solid var(--m-border);
  border-radius: 14px; padding: 12px;
}
.bnk-mtg-search {
  display: flex; align-items: center; gap: 8px;
  background: var(--m-recess); border: 1px solid var(--m-border);
  border-radius: 10px; padding: 0 10px; height: 36px; margin-bottom: 14px;
  transition: border-color .15s;
}
.bnk-mtg-search:focus-within { border-color: var(--m-border2); }
.bnk-mtg-search input {
  flex: 1; min-width: 0; background: transparent; border: 0; outline: none;
  color: var(--m-text); font: inherit; font-size: 12.5px;
}
.bnk-mtg-search input::placeholder { color: var(--m-ter); }
.bnk-mtg-filter {
  display: inline-flex; align-items: center; gap: 4px; flex: none;
  font-size: 11px; font-weight: 600; color: var(--m-sec);
  padding: 3px 8px; border-radius: 999px; background: rgba(255, 255, 255, 0.04);
}
.bnk-mtg-rail-head {
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--m-ter); margin: 0 2px 8px;
}
.bnk-mtg-list {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 6px;
  overflow-y: auto; min-height: 0;
  scrollbar-width: none;
}
.bnk-mtg-list::-webkit-scrollbar { width: 0; height: 0; }
.bnk-mtg-item {
  display: flex; align-items: stretch; gap: 0; width: 100%; cursor: pointer;
  text-align: left; font: inherit; padding: 0; overflow: hidden;
  background: transparent; border: 1px solid transparent; border-radius: 10px;
  transition: background .15s, border-color .15s;
}
.bnk-mtg-item:hover { background: rgba(255, 255, 255, 0.04); border-color: var(--m-border); }
.bnk-mtg-item-bar { width: 3px; flex: none; background: transparent; }
.bnk-mtg-item--active { background: var(--m-elev); border-color: rgba(45, 126, 255, 0.45); box-shadow: 0 0 0 1px rgba(45, 126, 255, 0.25); }
.bnk-mtg-item--active .bnk-mtg-item-bar { background: var(--m-primary); }
.bnk-mtg-item--active .bnk-mtg-item-title { color: var(--m-text); }
.bnk-mtg-item-body { display: flex; flex-direction: column; gap: 4px; padding: 9px 11px; min-width: 0; }
.bnk-mtg-item-title {
  font-size: 12.5px; font-weight: 600; color: var(--m-sec); line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.bnk-mtg-item-meta {
  display: flex; align-items: center; gap: 5px; flex-wrap: wrap;
  font-size: 11px; color: var(--m-ter); font-variant-numeric: tabular-nums;
}
.bnk-mtg-item-meta svg { color: var(--m-ter); }
.bnk-mtg-dotsep { color: var(--m-ter); }

/* ── CENTER ── */
.bnk-mtg-center {
  display: flex; flex-direction: column; min-height: 0; min-width: 0;
}
.bnk-mtg-meta {
  background: var(--m-surface); border: 1px solid var(--m-border);
  border-radius: 14px 14px 0 0; border-bottom: 0; padding: 16px 18px;
}
.bnk-mtg-meta-title { margin: 0; font-size: 17px; font-weight: 700; letter-spacing: -0.01em; }
.bnk-mtg-meta-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.bnk-mtg-meta-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: var(--m-sec);
  padding: 5px 11px; border-radius: 999px;
  background: var(--m-recess); border: 1px solid var(--m-border);
  font-variant-numeric: tabular-nums;
}
.bnk-mtg-meta-chip svg { color: var(--m-primary); }
.bnk-mtg-parts { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 14px; }
.bnk-mtg-parts-label { font-size: 11.5px; font-weight: 600; color: var(--m-ter); margin-right: 2px; }
.bnk-mtg-part {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12px; font-weight: 600;
  padding: 4px 11px 4px 4px; border-radius: 999px; border: 1px solid transparent;
}
.bnk-mtg-part-avatar {
  display: grid; place-items: center; width: 20px; height: 20px; flex: none;
  border-radius: 50%; font-size: 9.5px; font-weight: 700; color: #fff;
  letter-spacing: 0.02em;
}

.bnk-mtg-transcript {
  flex: 1; min-height: 0; display: flex; flex-direction: column;
  background: var(--m-recess); border: 1px solid var(--m-border);
  border-radius: 0 0 14px 14px; padding: 14px 16px;
}
.bnk-mtg-transcript-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.bnk-mtg-transcript-title {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12.5px; font-weight: 700; color: var(--m-text);
}
.bnk-mtg-transcript-title svg { color: var(--m-sec); }
.bnk-mtg-status {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600; color: var(--m-green);
  padding: 3px 10px; border-radius: 999px;
  background: rgba(52, 211, 153, 0.1); border: 1px solid rgba(52, 211, 153, 0.25);
}
.bnk-mtg-status-dot {
  width: 6px; height: 6px; border-radius: 50%; background: var(--m-green);
  box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.6); animation: bnk-mtg-pulse 2.4s ease-out infinite;
}
@keyframes bnk-mtg-pulse {
  0% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55); }
  70% { box-shadow: 0 0 0 6px rgba(52, 211, 153, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
}
.bnk-mtg-transcript-meta { font-size: 11px; color: var(--m-ter); margin: 5px 0 12px; }

.bnk-mtg-lines {
  flex: 1; min-height: 0; overflow-y: auto;
  display: flex; flex-direction: column; gap: 14px;
  padding-right: 4px; scrollbar-width: none;
}
.bnk-mtg-lines::-webkit-scrollbar { width: 0; height: 0; }
.bnk-mtg-line { display: flex; gap: 11px; }
.bnk-mtg-avatar {
  display: grid; place-items: center; width: 30px; height: 30px; flex: none; margin-top: 2px;
  border-radius: 9px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.02em;
  border: 1px solid transparent;
}
.bnk-mtg-line-body { min-width: 0; flex: 1; }
.bnk-mtg-line-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 3px; }
.bnk-mtg-speaker { font-size: 12.5px; font-weight: 700; }
.bnk-mtg-time { font-size: 11px; color: var(--m-ter); font-variant-numeric: tabular-nums; letter-spacing: 0.01em; }
.bnk-mtg-utter { margin: 0; font-size: 13px; line-height: 1.55; color: var(--m-sec); }

/* ── RIGHT summary ── */
.bnk-mtg-summary {
  display: flex; flex-direction: column; min-height: 0;
  background: var(--m-surface);
  border: 1px solid rgba(45, 126, 255, 0.3);
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 0 0 1px rgba(45, 126, 255, 0.08), 0 12px 32px rgba(0, 0, 0, 0.32);
}
.bnk-mtg-summary-head {
  display: flex; align-items: center; gap: 10px; flex: none;
  padding: 14px 16px;
  background: linear-gradient(180deg, rgba(45, 126, 255, 0.14) 0%, rgba(45, 126, 255, 0.02) 100%);
  border-bottom: 1px solid var(--m-border);
}
.bnk-mtg-summary-icon {
  display: grid; place-items: center; width: 30px; height: 30px; flex: none;
  border-radius: 9px; color: #fff;
  background: linear-gradient(135deg, ${C.primary}, ${C.cyan});
  box-shadow: 0 4px 12px rgba(45, 126, 255, 0.4);
}
.bnk-mtg-summary-title { margin: 0; font-size: 14.5px; font-weight: 700; flex: 1; letter-spacing: -0.01em; }
.bnk-mtg-summary-badge {
  display: inline-flex; align-items: center; flex: none;
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.02em; color: var(--m-cyan);
  padding: 3px 9px; border-radius: 999px;
  background: rgba(0, 212, 255, 0.1); border: 1px solid rgba(0, 212, 255, 0.3);
}

.bnk-mtg-summary-scroll {
  flex: 1; min-height: 0; overflow-y: auto;
  padding: 16px; display: flex; flex-direction: column; gap: 20px;
  scrollbar-width: none;
}
.bnk-mtg-summary-scroll::-webkit-scrollbar { width: 0; height: 0; }
.bnk-mtg-block { display: flex; flex-direction: column; gap: 10px; }
.bnk-mtg-block-head {
  display: flex; align-items: center; gap: 8px; margin: 0;
  font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--m-sec);
}
.bnk-mtg-count {
  display: inline-grid; place-items: center; min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px; font-size: 10.5px; font-weight: 700; color: var(--m-primary);
  background: rgba(45, 126, 255, 0.14); border: 1px solid rgba(45, 126, 255, 0.3);
  letter-spacing: 0;
}

/* decisions */
.bnk-mtg-decisions { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 9px; }
.bnk-mtg-decision {
  display: flex; gap: 10px; font-size: 12.5px; line-height: 1.5; color: var(--m-text);
}
.bnk-mtg-decision-mark {
  flex: none; width: 6px; height: 6px; margin-top: 6px; border-radius: 50%;
  background: ${C.amber}; box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.16);
}

/* todos (checklist) */
.bnk-mtg-todos { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 9px; }
.bnk-mtg-todo {
  display: flex; gap: 10px; padding: 11px 12px;
  background: var(--m-recess); border: 1px solid var(--m-border); border-radius: 11px;
  transition: border-color .15s, transform .15s;
}
.bnk-mtg-todo:hover { border-color: var(--m-border2); transform: translateY(-1px); }
.bnk-mtg-check { flex: none; color: var(--m-green); margin-top: 1px; }
.bnk-mtg-todo-body { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.bnk-mtg-todo-task { font-size: 12.5px; font-weight: 600; line-height: 1.45; color: var(--m-text); }
.bnk-mtg-todo-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 7px; }
.bnk-mtg-todo-owner {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; padding: 3px 9px;
  border-radius: 999px; border: 1px solid transparent;
}
.bnk-mtg-todo-due {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; color: var(--m-sec);
  font-variant-numeric: tabular-nums;
}
.bnk-mtg-todo-due svg { color: ${C.red}; }

/* next steps */
.bnk-mtg-next { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 9px; }
.bnk-mtg-next-item {
  display: flex; gap: 8px; font-size: 12.5px; line-height: 1.5; color: var(--m-text);
}
.bnk-mtg-next-item svg { flex: none; margin-top: 2px; color: var(--m-cyan); }

/* footer */
.bnk-mtg-summary-foot {
  display: flex; align-items: center; gap: 8px; flex: none; flex-wrap: wrap;
  padding: 12px 16px 0;
}
.bnk-mtg-source {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600; color: var(--m-sec);
  padding: 4px 10px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid var(--m-border);
}
.bnk-mtg-source svg { color: var(--m-sec); }
.bnk-mtg-verified {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600; color: var(--m-green);
  padding: 4px 10px; border-radius: 999px;
  background: rgba(52, 211, 153, 0.1); border: 1px solid rgba(52, 211, 153, 0.25);
}
.bnk-mtg-summary-note {
  display: flex; align-items: center; gap: 6px; flex: none;
  margin: 10px 16px 14px; font-size: 11px; color: var(--m-ter); line-height: 1.4;
}
.bnk-mtg-summary-note svg { flex: none; color: var(--m-green); }

/* ── responsive: collapse to a single readable column on narrow shells ── */
@media (max-width: 1080px) {
  .bnk-mtg-grid { grid-template-columns: 200px minmax(0, 1fr); }
  .bnk-mtg-summary { grid-column: 1 / -1; }
}
@media (max-width: 760px) {
  .bnk-mtg-grid { grid-template-columns: 1fr; }
  .bnk-mtg-rail, .bnk-mtg-summary { grid-column: auto; }
}

@media (prefers-reduced-motion: reduce) {
  .bnk-meeting--active .bnk-mtg-head,
  .bnk-meeting--active .bnk-mtg-rail,
  .bnk-meeting--active .bnk-mtg-center,
  .bnk-meeting--active .bnk-mtg-summary { animation: none; }
  .bnk-mtg-status-dot { animation: none; }
  .bnk-mtg-todo { transition: none; }
}
`;
